import React from 'react';
import {
  getPasswordStrength,
  getPasswordStrengthLabel,
  type PasswordStrengthResult,
} from '../../schemas/auth.schema';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

export const PasswordStrengthIndicator: React.FC<
  PasswordStrengthIndicatorProps
> = ({ password, showRequirements = true }) => {
  const strength: PasswordStrengthResult = getPasswordStrength(password);
  const { label, colorClass } = getPasswordStrengthLabel(strength.score);

  // Only show if password has been entered
  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-600">Password strength:</span>
          <span className={`text-xs font-medium ${colorClass}`}>{label}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${
              strength.score === 0
                ? 'bg-red-600 w-1/5'
                : strength.score === 1
                  ? 'bg-orange-600 w-2/5'
                  : strength.score === 2
                    ? 'bg-yellow-600 w-3/5'
                    : strength.score === 3
                      ? 'bg-blue-600 w-4/5'
                      : 'bg-green-600 w-full'
            }`}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      {showRequirements && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Requirements:</p>
          <div className="grid grid-cols-1 gap-1 text-xs">
            <RequirementItem
              met={strength.requirements.minLength}
              text="At least 8 characters"
            />
            <RequirementItem
              met={strength.requirements.hasLowercase}
              text="One lowercase letter"
            />
            <RequirementItem
              met={strength.requirements.hasUppercase}
              text="One uppercase letter"
            />
            <RequirementItem
              met={strength.requirements.hasNumber}
              text="One number"
            />
            <RequirementItem
              met={strength.requirements.hasSpecialChar}
              text="One special character (!@#$%^&*)"
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface RequirementItemProps {
  met: boolean;
  text: string;
}

const RequirementItem: React.FC<RequirementItemProps> = ({ met, text }) => {
  return (
    <div className="flex items-center gap-1.5">
      {met ? (
        <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      )}
      <span className={met ? 'text-green-700' : 'text-gray-500'}>{text}</span>
    </div>
  );
};

export default PasswordStrengthIndicator;
